"use client";

import { useEffect, useRef, useMemo, ReactNode } from "react";
import { gsap } from "@/lib/gsap";

interface AnimationSettings {
  duration?: number;
  imageStartPosition?: number;
  imageScale?: number;
  maskStartPosition?: number;
  ease?: string;
  threshold?: number;
}

interface AnimatedImageProps {
  children: ReactNode;
  className?: string;
  animationSettings?: AnimationSettings;
}

export default function AnimatedImage({
  children,
  className = "",
  animationSettings = {},
}: AnimatedImageProps) {
  const maskRef = useRef<HTMLDivElement>(null);

  const settings = useMemo(() => {
    const defaultSettings: Required<AnimationSettings> = {
      duration: 3,
      imageStartPosition: -140,
      imageScale: 1,
      maskStartPosition: 80,
      ease: "power2.out",
      threshold: 0.5,
    };
    return { ...defaultSettings, ...animationSettings };
  }, [animationSettings]);

  useEffect(() => {
    if (!maskRef.current) {
      return;
    }

    const mask = maskRef.current;
    const image = mask.querySelector("img");

    if (!image) {
      return;
    }

    const ctx = gsap.context(() => {
      gsap.set(mask, {
        xPercent: settings.maskStartPosition,
        autoAlpha: 1,
      });
      gsap.set(image, {
        xPercent: settings.imageStartPosition,
        scale: settings.imageScale,
      });
    }, mask);

    let lastScrollY = 0;
    let hasAnimated = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentScrollY = window.scrollY;
          const isScrollingDown = currentScrollY > lastScrollY;

          if (entry.isIntersecting) {
            if (isScrollingDown && !hasAnimated) {
              gsap.set(mask, {
                xPercent: settings.maskStartPosition,
              });
              gsap.set(image, {
                xPercent: settings.imageStartPosition,
                scale: settings.imageScale,
              });

              gsap.to(image, settings.duration, {
                xPercent: 0,
                scale: 1,
                ease: settings.ease,
              });
              gsap.to(mask, settings.duration, {
                xPercent: 0,
                ease: settings.ease,
              });

              hasAnimated = true;
            } else if (!isScrollingDown) {
              gsap.set(mask, {
                xPercent: 0,
              });
              gsap.set(image, {
                xPercent: 0,
                scale: 1,
              });
              hasAnimated = true;
            }
          } else {
            if (!isScrollingDown && entry.intersectionRatio < 0.05) {
              gsap.set(mask, {
                xPercent: settings.maskStartPosition,
              });
              gsap.set(image, {
                xPercent: settings.imageStartPosition,
                scale: settings.imageScale,
              });
              hasAnimated = false;
            }
          }

          lastScrollY = currentScrollY;
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] }
    );

    observer.observe(mask);

    return () => {
      ctx.revert();
      observer.disconnect();
    };
  }, [settings]);

  return (
    <div ref={maskRef} className={className}>
      {children}
    </div>
  );
}
